<script lang="ts">
	import { page } from '$app/state';
	import { browser } from '$app/environment';
	import GridLines from '$lib/components/GridLines.svelte';
	import GridContent from '$lib/components/GridContent.svelte';
	import GridInner from '$lib/components/GridInner.svelte';
	import { hatchStyle45 } from '$lib/styles/hatch';
	import { submitContact, submitNewsletter, LeadApiError } from '$lib/api/leads';

	type FormState = 'idle' | 'submitting' | 'success' | 'error';

	let formState = $state<FormState>('idle');
	let errorMessage = $state('');

	// Form fields
	let email = $state('');
	let name = $state('');
	let subject = $state(browser ? page.url.searchParams.get('subject') ?? '' : '');
	let message = $state('');

	// Validation errors
	let emailError = $state('');
	let messageError = $state('');

	// Newsletter opt-in
	let subscribeNewsletter = $state(false);

	function validateForm(): boolean {
		let valid = true;

		// Email - required + format
		if (!email.trim()) {
			emailError = 'Email is required';
			valid = false;
		} else {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(email)) {
				emailError = 'Please enter a valid email address';
				valid = false;
			} else {
				emailError = '';
			}
		}

		// Message - required
		if (!message.trim()) {
			messageError = 'Message is required';
			valid = false;
		} else {
			messageError = '';
		}

		return valid;
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		formState = 'submitting';
		errorMessage = '';

		try {
			// Submit contact form
			await submitContact({
				Email: email.trim(),
				Name: name.trim() || undefined,
				Subject: subject.trim() || undefined,
				Message: message.trim()
			});

			// Also subscribe to newsletter if opted in
			if (subscribeNewsletter) {
				await submitNewsletter(email.trim());
			}

			formState = 'success';
		} catch (err) {
			formState = 'error';
			if (err instanceof LeadApiError) {
				errorMessage = err.message;
			} else {
				errorMessage = 'Something went wrong. Please try again.';
			}
		}
	}

	function resetForm() {
		formState = 'idle';
		errorMessage = '';
		email = '';
		name = '';
		subject = '';
		message = '';
		emailError = '';
		messageError = '';
		subscribeNewsletter = false;
	}
</script>

<svelte:head>
	<title>Contact - Corral AI</title>
</svelte:head>

<section class="bg-[#f9f9f4] py-24 relative min-h-[60vh]" style={hatchStyle45}>
	<GridLines variant="light" />
	<GridContent>
		<GridInner>
			<div class="flex flex-col items-center text-center">
				<h1
					class="font-display text-[48px] lg:text-[58px] text-black tracking-[-0.04em] leading-[1.1] mb-5"
				>
					Contact Us
				</h1>
				<p class="font-body text-[16px] lg:text-[18px] text-[#5c5855] max-w-2xl">
					We're here to help. Whether you're evaluating Corral, planning a deployment, or just have
					questions about enterprise AI.
				</p>
			</div>

			<div class="mt-12 max-w-[600px] mx-auto">
				{#if formState === 'success'}
					<!-- Success State -->
					<div class="text-center py-12">
						<div class="font-display text-[32px] text-black mb-3">Message sent!</div>
						<p class="font-mono font-light text-[14px] text-[#5c5855] mb-8">
							We'll get back to you soon.
						</p>
						<button
							type="button"
							onclick={resetForm}
							class="font-mono text-[14px] text-[#5c5855] hover:text-black underline transition-colors"
						>
							Send another message
						</button>
					</div>
				{:else}
					<!-- Contact Form -->
					<form onsubmit={handleSubmit} class="space-y-5">
						{#if formState === 'error' && errorMessage}
							<div class="bg-red-50 border border-red-200 px-4 py-3 text-left rounded">
								<p class="font-mono text-[14px] text-red-600">{errorMessage}</p>
								<button
									type="button"
									onclick={() => (errorMessage = '')}
									class="font-mono text-[12px] text-red-400 hover:text-red-600 underline mt-1"
								>
									Dismiss
								</button>
							</div>
						{/if}

						<!-- Email (Required) -->
						<div class="text-left">
							<label
								for="contact-email"
								class="font-mono text-[12px] text-[#5c5855] uppercase tracking-wider block mb-2"
							>
								Email <span class="text-black">*</span>
							</label>
							<input
								id="contact-email"
								type="email"
								bind:value={email}
								required
								disabled={formState === 'submitting'}
								class="w-full bg-white border border-gray-300 px-4 py-3 font-body text-[16px] text-black placeholder:text-[#9ca3af] focus:outline-none focus:border-black/50 transition-colors disabled:opacity-50 rounded"
								placeholder="your@email.com"
							/>
							{#if emailError}
								<p class="font-mono text-[12px] text-red-500 mt-1">{emailError}</p>
							{/if}
						</div>

						<!-- Name (Optional) -->
						<div class="text-left">
							<label
								for="contact-name"
								class="font-mono text-[12px] text-[#5c5855] uppercase tracking-wider block mb-2"
							>
								Name
							</label>
							<input
								id="contact-name"
								type="text"
								bind:value={name}
								disabled={formState === 'submitting'}
								class="w-full bg-white border border-gray-300 px-4 py-3 font-body text-[16px] text-black placeholder:text-[#9ca3af] focus:outline-none focus:border-black/50 transition-colors disabled:opacity-50 rounded"
								placeholder="Your name"
							/>
						</div>

						<!-- Subject (Optional) -->
						<div class="text-left">
							<label
								for="contact-subject"
								class="font-mono text-[12px] text-[#5c5855] uppercase tracking-wider block mb-2"
							>
								Subject
							</label>
							<input
								id="contact-subject"
								type="text"
								bind:value={subject}
								disabled={formState === 'submitting'}
								class="w-full bg-white border border-gray-300 px-4 py-3 font-body text-[16px] text-black placeholder:text-[#9ca3af] focus:outline-none focus:border-black/50 transition-colors disabled:opacity-50 rounded"
								placeholder="What's this about?"
							/>
						</div>

						<!-- Message (Required) -->
						<div class="text-left">
							<label
								for="contact-message"
								class="font-mono text-[12px] text-[#5c5855] uppercase tracking-wider block mb-2"
							>
								Message <span class="text-black">*</span>
							</label>
							<textarea
								id="contact-message"
								bind:value={message}
								required
								disabled={formState === 'submitting'}
								rows={5}
								class="w-full bg-white border border-gray-300 px-4 py-3 font-body text-[16px] text-black placeholder:text-[#9ca3af] focus:outline-none focus:border-black/50 transition-colors disabled:opacity-50 resize-none rounded"
								placeholder="How can we help?"
							></textarea>
							{#if messageError}
								<p class="font-mono text-[12px] text-red-500 mt-1">{messageError}</p>
							{/if}
						</div>

						<!-- Newsletter Opt-in -->
						<div class="text-left">
							<label class="flex items-start gap-3 cursor-pointer group">
								<input
									type="checkbox"
									bind:checked={subscribeNewsletter}
									disabled={formState === 'submitting'}
									class="mt-1 w-4 h-4 bg-white border border-gray-300 rounded appearance-none checked:bg-black checked:border-black cursor-pointer disabled:opacity-50"
								/>
								<span
									class="font-mono text-[13px] text-[#5c5855] leading-relaxed group-hover:text-[#333] transition-colors"
								>
									Sign up for our newsletter? Once a week max, no spam. Just keeps you updated with
									what we're doing.
								</span>
							</label>
						</div>

						<!-- Submit Button -->
						<div class="pt-4">
							<button
								type="submit"
								disabled={formState === 'submitting'}
								class="bg-black text-white font-mono text-[14px] h-[48px] px-10 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								style="box-shadow: 0px 0px 14.7px 5px rgba(255, 255, 255, 0.35) inset;"
							>
								{#if formState === 'submitting'}
									Sending...
								{:else}
									Send message
								{/if}
							</button>
						</div>
					</form>
				{/if}
			</div>
		</GridInner>
	</GridContent>
</section>
